import { useCallback, useEffect } from 'react';

import { useNonRevalidatingFetcher } from '~/hooks/useNonRevalidatingFetcher';
import type { GroupSubscriptionSeatsLoaderData } from '~/routes/__loaders/group_subscription_seats';

// The reduction picker needs the whole candidate pool in one shot (selection
// spans the list, so paging would strand off-page picks). Group seat counts are
// small enough that a single large page is fine; mirrors what the modal renders.
const CANDIDATE_PER_PAGE = 200;

export type UseReductionCandidatesResult = {
  /** Filled + terminating seats — the seats eligible to (newly) terminate, plus
   *  the already-scheduled ones so edit/swap can show current state. */
  seats: GroupSubscriptionSeatsLoaderData['seats'];
  isLoading: boolean;
};

/**
 * Fetches the seat pool for the "select members to remove" picker. Only filled
 * and terminating seats can be involved in a reduction, so we scope the fetch to
 * those rather than reusing the card's paginated, all-status loader.
 */
export function useReductionCandidates(groupId: string | null, enabled: boolean): UseReductionCandidatesResult {
  const fetcher = useNonRevalidatingFetcher<GroupSubscriptionSeatsLoaderData>();

  const load = useCallback(() => {
    if (!enabled || !groupId) return;
    const params = new URLSearchParams({
      group_subscription_id: groupId,
      status: 'filled,terminating',
      page: '1',
      per_page: String(CANDIDATE_PER_PAGE),
    });
    fetcher.load(`/group_subscription_seats?${params.toString()}`);
    // biome-ignore lint/correctness/useExhaustiveDependencies: fetcher in deps loops forever (matches useGroupSeats).
  }, [enabled, groupId]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    seats: fetcher.data?.seats ?? [],
    isLoading: fetcher.state === 'loading',
  };
}
